import { useState } from "react";
import toast from "react-hot-toast";
import * as _ from "lodash";
import * as Sentry from "@sentry/react";
import { useIsMutating } from "@tanstack/react-query";

import { useCompanyFlow } from "@/hooks/useCompanyFlow";
import { useProductStore } from "@/_zustand/store";
import { useMachine } from "@/_zustand/machine";
import { useAuth } from "@/_zustand/auth";
import { useCrearPreferenciaMP } from "@/hooks/useMercadoPago";
import { getEnvVariables } from "@/constants/getEnvVariables";
import { buildAppliedDiscount } from "@/lib";
import {
  verificarTotalPreferencia,
  guardarSnapshotMP,
  generarBuyOrderMP,
  MP_MSG_DESCUADRE,
} from "@/lib/mpCheckout";

export const MercadoPagoButton = () => {
  const { DOMAIN } = getEnvVariables();
  const { getCompanyRoute } = useCompanyFlow();
  const {
    buyOrder,
    setBuyOrder,
    products: cartProducts,
    descuento_id,
    descuento_data,
    total,
    discount,
  } = useProductStore();
  const { machineSelected: selectedMachine } = useMachine();
  const { user } = useAuth();
  const crearPreferenciaMP = useCrearPreferenciaMP();
  const isMutating = useIsMutating({ mutationKey: ["useCrearPreferenciaMP"] });
  const [isRedirecting, setIsRedirecting] = useState(false);

  const iniciarPago = () => {
    // Caso borde 5: carrito íntegramente cubierto por el cupón. Mercado Pago
    // rechaza montos <= 0; encauzar a "pagar con descuento" en vez de crear
    // una preferencia inválida.
    if (Math.max(0, Math.round(total) - Math.round(discount)) <= 0) {
      toast.error(
        "El total del pedido es $0, usa la opción de pagar con descuento",
      );
      return;
    }

    const cart = _.filter(
      cartProducts,
      (item) => item.maquina_id == selectedMachine && Number(item.producto_cant) > 0,
    ).map((item) => ({
      producto_id: item.producto_id,
      cantidad: item.producto_cant,
      descuento_precio_final: item.descuento_precio_final,
      // Ofertas directas de producto (sin % ni monto fijo) sólo viven en este
      // campo — sin él, crearPreferencia cobraba el precio base completo.
      producto_oferta: item.producto_oferta,
      // 2x1 propio del producto: sin este campo, crearPreferencia cobraba
      // la cantidad completa en vez de ceil(cantidad/2), igual que
      // getTotal()/hasOffers() en CartContext.js (Sima-appmovil).
      descuento_dosxuno: item.descuento_dosxuno,
    }));

    const redirectUrl = `${DOMAIN}${getCompanyRoute("mp-return")}`;

    setIsRedirecting(true);

    // El backend (crearPreferencia) revalida el cupón contra descuento_id y
    // reparte su descuento entre los items antes de cobrar — igual que
    // buildAppliedDiscount ya hace para Webpay/OneClick/createOrder.
    crearPreferenciaMP
      .mutateAsync({
        buyOrder,
        cart,
        payerEmail: user.usuario_correo,
        redirectUrl,
        appliedDiscount: buildAppliedDiscount(descuento_data, descuento_id),
        // Con la máquina, crearPreferencia toma precios y ofertas de la BD e
        // ignora los del body (Sima-apirest a32bca06); sin ella cae al body y
        // registra [MP-PRECIO-SIN-MAQUINA].
        storeId: selectedMachine,
      })
      .then((resp) => {
        if (resp?.status !== "success" || !resp?.data?.preference?.init_point) {
          toast.error(resp?.message || "Error al iniciar Mercado Pago");
          setIsRedirecting(false);
          return;
        }

        // Salvaguarda BLOQUEANTE: compara el total esperado del carrito
        // contra el total que realmente arma crearPreferencia (el backend es
        // quien recalcula desde BD, ver Sima-apirest/crearPreferencia.post.ts),
        // igual que el patrón de getState() en
        // MercadoPagoReturnPage.jsx/OneClick.jsx, para leer el estado fresco.
        const { total: storeTotal, discount: storeDiscount } =
          useProductStore.getState();
        const totalEsperado = Math.max(
          0,
          Math.round(storeTotal) - Math.round(storeDiscount),
        );

        const { estado, totalPreferencia, diferencia } = verificarTotalPreferencia({
          totalPreferencia: resp.data.total,
          totalEsperado,
        });

        if (estado === "descuadre") {
          Sentry.captureMessage(
            "Descuadre entre total del carrito y preferencia de Mercado Pago",
            {
              level: "error",
              tags: { financial_risk: true, payment_flow: "mercadopago" },
              extra: {
                buyOrder,
                totalEsperado,
                totalPreferencia,
                diferencia,
                cart,
              },
            },
          );
          toast.error(MP_MSG_DESCUADRE);
          setIsRedirecting(false);
          // Evita reusar el external_reference de la preferencia huérfana que
          // acaba de crearse en Mercado Pago (caso borde 3).
          setBuyOrder(generarBuyOrderMP());
          return;
        }

        if (
          estado === "no_verificable" &&
          descuento_id != null &&
          Number(descuento_id) > 0
        ) {
          // Backend que no devuelve un total verificable, con un cupón
          // aplicado: no hay descuadre probado ni cargo confirmado, así que
          // no bloquea, pero se reporta como advertencia (paridad con
          // mp_backend_sin_pricingVersion2 de MetodoPagoScreen.js).
          Sentry.captureMessage(
            "Backend de Mercado Pago no devolvió un total verificable",
            {
              level: "warning",
              tags: { payment_flow: "mercadopago" },
              extra: { buyOrder, totalEsperado, descuento_id },
            },
          );
        }

        sessionStorage.setItem("mp_buyOrder", buyOrder);
        sessionStorage.setItem("mp_storeId", selectedMachine);
        guardarSnapshotMP({
          buyOrder,
          storeId: selectedMachine,
          total: totalEsperado,
          subtotal: storeTotal,
          descuento: storeDiscount,
        });

        // Siempre init_point — sandbox_init_point no genera pagos verificables
        window.location.href = resp.data.preference.init_point;
      })
      .catch(() => {
        toast.error("Error al conectar con Mercado Pago");
        setIsRedirecting(false);
      });
  };

  return (
    <button
      type="submit"
      className="order-btn-fill"
      style={{ minWidth: "150px" }}
      disabled={isMutating > 0 || isRedirecting}
      onClick={iniciarPago}
    >
      Pagar
    </button>
  );
};
